import helpers from '../helpers';
import _ from 'lodash';
import { DOCUMENT_TYPES } from '../constants';
import { DOCUMENT_TYPE, SERVICES_CODES, SERVICES_GROUPS } from 'app/config/services';
import moment from 'moment/moment';

export const preparedFormData = (preparedPayload) => {
    if (preparedPayload.payload) {
        const sendingData = new FormData();

        Object.keys(preparedPayload.payload).forEach((k) => {
            sendingData.append(k, preparedPayload.payload[k]);
        });

        return sendingData;
    }
    return false;
};

export const validateFormikForm = (formProps, validationSchema) => {
    const requiredFields = validationSchema['_nodes'];
    requiredFields.forEach((field) => {
        if (field) formProps.setFieldTouched(field, true);
    });
    return formProps.validateForm();
};

/**
 * Função que será usada como filtro para documentos em solicitações de alteração de email.
 * Se é pessoa fisica, remove documentos específicos para esse tipo. O mesmo para pessoa jurídica.
 * @param {Document} doc
 * @param {ChangeEmailPayload} values
 * @returns {boolean}
 */
export const filterDocumentsChangeEmail = (doc, values) => {
    const deCpfCnpj = values?.deCpfCnpj;
    if (deCpfCnpj) {
        const documento = helpers.somenteAlfanumericos(deCpfCnpj);
        if (documento.length === 11 && /^\d+$/.test(documento)) {
            return doc.cdTipoDocumento !== DOCUMENT_TYPE.CNPJ;
        }
        if (documento.length === 14) {
            return doc.cdTipoDocumento !== DOCUMENT_TYPE.CPF;
        }
    }
    return true;
};

const cpfCnpjFields = ['deCpf', 'deCpfCnpj', 'deCnpjMatriz', 'deCnpjFilial'];
const numericFields = ['deCep', 'deTelefone', 'deTelefonePrincipal', 'deTelefoneSecudario'];

const cleanProperty = (value, property) => {
    if (!value) return value;
    if (cpfCnpjFields.includes(property)) {
        return helpers.somenteAlfanumericos(value);
    }
    if (numericFields.includes(property)) {
        return helpers.somenteNumeros(value);
    }
    return value;
};

const propertiesToClean = [...cpfCnpjFields, ...numericFields];

const propertiesToFixHoursInDate = ['dtViagem', 'dtRetorno', 'dtVencimento'];

/**
 *
 * @param {Object} values
 * @param {Document} documents
 * @param {*} params
 * @param {Company} activeCompany
 * @param {User} userAdmin
 * @param {RegistrationUserInfo} registrationUserInfo
 * @param {boolean} isInternalNewRequest
 * @param {number} cdServico
 * @returns {Object}
 */
export const preparePayload = ({
    values,
    documents, //{ store: documentsStore, files: filesStored }
    params,
    activeCompany,
    userAdmin,
    registrationUserInfo,
    isInternalNewRequest,
    cdServico,
}) => {
    const payload = _.cloneDeep(params);
    let valuesCopy = _.cloneDeep(values);
    let errors = false;

    for (const property of propertiesToClean) {
        valuesCopy[property] = cleanProperty(valuesCopy[property], property);
    }

    for (const property of propertiesToFixHoursInDate) {
        valuesCopy[property] = setDateToNoon(valuesCopy[property], false);
    }

    if (valuesCopy.enderecoMatriz?.deCep) {
        valuesCopy.enderecoMatriz.deCep = cleanProperty(valuesCopy.enderecoMatriz.deCep, 'deCep');
    }

    if (valuesCopy.enderecoFilial?.deCep) {
        valuesCopy.enderecoFilial.deCep = cleanProperty(valuesCopy.enderecoFilial.deCep, 'deCep');
    }

    if (valuesCopy.enderecoPessoa?.deCep) {
        valuesCopy.enderecoPessoa.deCep = cleanProperty(valuesCopy.enderecoPessoa.deCep, 'deCep');
    }

    if (valuesCopy.contatos && valuesCopy.contatos.length > 0) {
        valuesCopy.contatos = valuesCopy.contatos.map((contact) => ({
            ...contact,
            deTelefoneFixo: helpers.somenteNumeros(contact.deTelefoneFixo),
            deTelefoneMovel: helpers.somenteNumeros(contact.deTelefoneMovel),
        }));
    }

    if (valuesCopy.enderecoMatriz) {
        valuesCopy.enderecoMatriz = trimObjectProperties(valuesCopy.enderecoMatriz);
        if (valuesCopy.enderecoFilial) {
            if (valuesCopy.enderecoMatriz.cdEnderecoEmpresa === valuesCopy.enderecoFilial.cdEnderecoEmpresa) {
                delete valuesCopy.enderecoFilial.cdEnderecoEmpresa;
            }
            valuesCopy.enderecoFilial = trimObjectProperties(valuesCopy.enderecoFilial);
        }
    }

    if (valuesCopy.enderecoPessoa) {
        valuesCopy.enderecoPessoa = trimObjectProperties(valuesCopy.enderecoPessoa);
    }

    // workaround para problemas de serialização do jackson!
    // como este workaround tem precedentes (fim desta função), repliquei ao invés de solucionar o problema... :(
    if (valuesCopy.veiculos) {
        for (let veiculo of valuesCopy.veiculos) {
            if (veiculo.dadosOriginais) {
                delete veiculo.dadosOriginais.dtCadastro;
                delete veiculo.dadosOriginais.dtAlteracao;
            }
        }
    }

    valuesCopy = trimObjectProperties(valuesCopy);

    if (!SERVICES_GROUPS.COMPANY.includes(payload.cdServico) && !isInternalNewRequest) {
        if (
            (!valuesCopy.deCpfCnpj || valuesCopy.deCpfCnpj === '') &&
            (!valuesCopy.nmCompleto || valuesCopy.nmCompleto === '')
        ) {
            if (activeCompany && Object.keys(activeCompany).length > 0) {
                valuesCopy.deCpfCnpj = activeCompany.cdTipoPessoaFisica
                    ? activeCompany.deCpf
                    : activeCompany.deCnpjMatriz;
            } else if (userAdmin && Object.keys(userAdmin).length > 0) {
                valuesCopy.deCpfCnpj = userAdmin.deCpfCnpj;
            } else {
                valuesCopy.deCpfCnpj = registrationUserInfo?.deCpfCnpj;
            }
        }

        if (!valuesCopy.nmCompleto || valuesCopy.nmCompleto === '') {
            if (activeCompany && Object.keys(activeCompany).length > 0) {
                valuesCopy.nmCompleto = activeCompany.cdTipoPessoaFisica
                    ? activeCompany.nmCompleto
                    : activeCompany.deRazaoSocial;
            } else if (userAdmin && Object.keys(userAdmin).length > 0) {
                valuesCopy.nmCompleto = userAdmin.nmUsuario;
            } else {
                valuesCopy.nmCompleto = registrationUserInfo?.nmUsuario;
            }
        }

        if (activeCompany?.cdTipoPessoaFisica && !valuesCopy.cdTipoPessoaFisica) {
            valuesCopy.cdTipoPessoaFisica = activeCompany.cdTipoPessoaFisica;
        }

        if (activeCompany?.cdTipoEmpresa && !valuesCopy.cdTipoEmpresa) {
            valuesCopy.cdTipoEmpresa = activeCompany.cdTipoEmpresa;
        }
    }

    if (payload.cdServico !== SERVICES_CODES.INSCRICAO_CADASTRAL) {
        if (valuesCopy.cdEmpresa) {
            payload.cdEmpresa = valuesCopy.cdEmpresa;
        } else if (activeCompany && activeCompany.cdEmpresa && !isInternalNewRequest) {
            payload.cdEmpresa = activeCompany.cdEmpresa;
        }
    }

    if (valuesCopy.cdUsuarioResponsavelTmp && valuesCopy.cdUsuarioResponsavelTmp !== '') {
        payload.cdUsuarioResponsavelTmp = valuesCopy.cdUsuarioResponsavelTmp;
        delete valuesCopy.cdUsuarioResponsavelTmp;
    }

    if (documents && documents.store && documents.files) {
        //VALIDATION
        documents.store
            .filter((doc) =>
                SERVICES_CODES.CHANGE_EMAIL === cdServico ? filterDocumentsChangeEmail(doc, values) : true,
            )
            .forEach((doc) => {
                const file = documents.files.filter((f) => f.cdTipoDocumento === doc.cdTipoDocumento);
                if (doc.boDocumentoObrigatorio) {
                    if (file.length === 0) {
                        errors = true;
                    } else {
                        file.forEach((f) => {
                            if (f.boExcluido) {
                                errors = true;
                                return;
                            }
                        });
                    }
                }
                if (errors) {
                    return;
                }
            });
    } else {
        errors = true;
    }

    if (valuesCopy.ultimaSolicitacao) delete valuesCopy.ultimaSolicitacao;
    if (payload.dtCadastro) delete payload.dtCadastro;
    if (payload.dtAlteracao) delete payload.dtAlteracao;

    payload.jsonSolicitacao = JSON.stringify(valuesCopy);
    return { payload: payload, errors: errors };
};

export const validateDocumentsDescription = (docs) => {
    let errors = 0;
    if (docs && docs.length > 0) {
        docs.forEach((doc) => {
            if (!doc.boExcluido) {
                if (doc.cdTipoDocumento && doc.cdTipoDocumento === DOCUMENT_TYPES.OTHERS) {
                    if (!doc.deDescricao || doc.deDescricao === '' || !doc.idFile || doc.idFile === '') {
                        errors++;
                    }
                } else if (!doc.cdTipoDocumento) {
                    if (!doc.deDescricao || doc.deDescricao === '' || !doc.idFile || doc.idFile === '') {
                        errors++;
                    }
                }
            }
        });
    }
    return errors === 0;
};

export const trimObjectProperties = (obj) => {
    Object.keys(obj).map((k) => (obj[k] = typeof obj[k] == 'string' ? obj[k].trim() : obj[k]));
    return obj;
};

export const validateDocumentation = (documents = [], listDocumentsType = []) => {
    if (documents.length === 0) {
        return false;
    }
    const errors = listDocumentsType.filter((type) => {
        if (type.boDocumentoObrigatorio) {
            const found = _.find(documents, ['cdTipoDocumento', type.cdTipoDocumento]);
            if (!found) return true;
        }
        return false;
    });
    return errors.length === 0;
};

/**
 * Função para setar o horário para meio-dia, evitando problema com conversão incorreta de timezone
 * @param _date - data original a ser convertida
 * @param isReturnString - espera uma ‘string’ como retorno
 * @returns {*|moment.Moment|string}
 */
export const setDateToNoon = (_date, isReturnString = true) => {
    if (!_date || _date === '') return _date;
    try {
        const parsedDate = moment.utc(_date).format('YYYY-MM-DD');
        if (parsedDate) {
            const _newDate = `${parsedDate} 12:00:00`;
            if (isReturnString) return _newDate;
            return moment(_newDate);
        }
    } catch (e) {
        console.error(e);
    }
    return _date;
};
